import type { AspectRatio } from '@lucimark/shared/database/custom-types/aspect-ratio'
import type { PlaybackSegment } from '@lucimark/shared/helpers/playbackScheduling'
import type { IPlaybackScheduleRoot } from '@lucimark/shared/models/player-registration-payload'
import type { TAssetsService } from '@/services/AssetsService'
import type { Logger } from '@/services/logger'
import type { IQueueFrame, TCampaignAsset } from '@/types'
import { registrationScheduleFrameOverlaysFingerprint } from '@lucimark/shared/helpers/embeddedPlaylistFramesSelectionFingerprint'
import { getPlaybackScheduleMaterializationIdentity, isScheduleRootAssignment } from '@lucimark/shared/helpers/playbackScheduleMaterializationIdentity'
import { resetEmbeddedFrameSelectionState } from '@lucimark/shared/helpers/selectEmbeddedFrames'
import Emittery from 'emittery'
import { QUEUE_REBUILD_INTERVAL_MS } from '@/constants'
import { collectScheduleRootAssetUrns } from '@/helpers/collectScheduleRootAssetUrns'
import {
  collectReadyWithoutBinaryUrns,
  filterQueueToCachedMedia
} from '@/helpers/filterQueueToCachedMedia'
import { getLocalAsUtcInstant } from '@/helpers/getLocalAsUtcInstant'
import { observeQueueDurationSpineInvariant } from '@/helpers/playerDiagnostics/observeQueueDurationSpineInvariant'
import { runPlayerDiagnosticsSafely } from '@/helpers/playerDiagnostics/playerDiagnosticService'
import {
  areCampaignAssetListsEquivalent,
  getQueueHash,
  getQueueSequenceHash,
  isSameQueueSequence
} from '@/helpers/queueHash'
import PlaybackScheduleLoader from '@/services/PlaybackScheduleLoader'
import { canonicalStringify } from '@/workers/registrationStatusWorkerHash'

export enum PlaybackScheduleMaterializationState {
  IDLE = 'idle',
  LOADING = 'loading',
  READY = 'ready',
  ERROR = 'error'
}

/** Options for {@link PlaybackScheduleMaterializationService.setPlaybackSchedule}. */
export type TSetPlaybackScheduleOptions = {
  /**
   * Wake / cache-repair recovery: always re-run `load()` + `fetchAssets` even when the assignment
   * JSON is byte-identical to the last applied root (Refs #1356).
   */
  forceAssetReload?: boolean
  /**
   * Soft-defer rematch: only these URNs may block on network (ready-gate head). Other schedule
   * URNs use local IDB/hybrid; missing preload/tail downloads in the background.
   */
  awaitAssetUrns?: readonly string[]
}

export enum PlaybackScheduleMaterializationEvent {
  QUEUE_UPDATE = 'queue-update',
  ASSETS_UPDATE = 'assets-update',
  STATE_UPDATE = 'state-update',
  LOAD_ERROR = 'load-error',
  /** Playable loop unchanged; count of frames still waiting on a binary changed. */
  SKIPPED_UNCACHED = 'skipped-uncached'
}

export interface PlaybackScheduleMaterializationEventMap {
  [PlaybackScheduleMaterializationEvent.QUEUE_UPDATE]: IQueueFrame[]
  [PlaybackScheduleMaterializationEvent.ASSETS_UPDATE]: TCampaignAsset[]
  [PlaybackScheduleMaterializationEvent.STATE_UPDATE]: PlaybackScheduleMaterializationState
  [PlaybackScheduleMaterializationEvent.LOAD_ERROR]: { message: string }
  [PlaybackScheduleMaterializationEvent.SKIPPED_UNCACHED]: number
}

export default class PlaybackScheduleMaterializationService extends Emittery<PlaybackScheduleMaterializationEventMap> {
  private logger: Logger
  private readonly playback: PlaybackScheduleLoader
  _assets: TCampaignAsset[] = []
  /** Editorial queue from the loader, including frames still waiting on a binary. */
  private _scheduledQueue: IQueueFrame[] = []
  /** Playable subset emitted to the scheduler (`queue` getter). */
  private _queue: IQueueFrame[] = []
  private _skippedUncachedFrameCount = 0
  private _state: PlaybackScheduleMaterializationState = PlaybackScheduleMaterializationState.IDLE
  private _queueRebuildIntervalId: ReturnType<typeof setInterval> | null = null
  /** Last {@link scheduleSpineFingerprintForRoot} applied to the materialized queue; gates interval rebuild noise. */
  private _lastQueueRebuildScheduleFp: string | null = null
  /** Serializes setPlaybackSchedule so recovery reload + CAMPAIGN_UPDATE cannot interleave loads. */
  private _setPlaybackScheduleChain: Promise<void> = Promise.resolve()
  /**
   * Latest schedule root requested by {@link setPlaybackSchedule}. `undefined` = no pending apply;
   * `null` = explicitly clear. Rapid calls overwrite so only the last root is applied once the chain runs.
   */
  private _coalescePending: IPlaybackScheduleRoot | null | undefined = undefined
  /** OR of `forceAssetReload` across coalesced callers until the next drain. */
  private _coalesceForceAssetReload = false
  /** Latest soft-defer head/preload gate across coalesced callers (cleared on drain). */
  private _coalesceAwaitAssetUrns: readonly string[] | undefined = undefined

  constructor(params: {
    assetsService: TAssetsService
    logger: Logger
  }) {
    super()
    const { assetsService, logger } = params
    this.playback = new PlaybackScheduleLoader({
      assetsService,
      onAssetsChange: (assets) => {
        this.assets = assets
      }
    })
    this.logger = logger
  }

  get state() {
    return this._state
  }

  set state(state: PlaybackScheduleMaterializationState) {
    this._state = state
    void this.emit(PlaybackScheduleMaterializationEvent.STATE_UPDATE, this._state)
  }

  get queue() {
    return this._queue
  }

  /** Frames omitted because their image/video binary is not cached yet. */
  get skippedUncachedFrameCount() {
    return this._skippedUncachedFrameCount
  }

  /**
   * Publishes `scheduled` after dropping binaries that are not cached yet.
   * Callers pass the editorial queue; {@link queue} is the playable subset the scheduler loops.
   */
  set queue(scheduled: IQueueFrame[]) {
    this._scheduledQueue = scheduled
    const { playable, skippedCount } = this._playableQueue(scheduled)
    const skippedChanged = skippedCount !== this._skippedUncachedFrameCount
    this._skippedUncachedFrameCount = skippedCount
    /**
     * `load()` often returns a fresh array with the same frames as after a benign registration-status poll.
     * Emitting {@link PlaybackScheduleMaterializationEvent.QUEUE_UPDATE} in that case re-triggers VBuffer watches
     * and visible flicker even though `getQueueHash` matches.
     *
     * Do **not** suppress the emit on {@link isSameQueueSequence} alone: that check is id+duration only and
     * hides facet.asset swaps after same-identity playlist edits, leaving `state.queue` stale while assets
     * refresh (#1352). Scheduler soft-paths (e.g. idsOnlyRefresh) still avoid loop reset when the sequence
     * is unchanged. Len-1 house-embed mid-dwell swaps are blocked in {@link startQueueRebuildTick}.
     */
    if (getQueueHash(this._queue) === getQueueHash(playable)) {
      this._queue = playable
      if (skippedChanged) {
        void this.emit(PlaybackScheduleMaterializationEvent.SKIPPED_UNCACHED, skippedCount)
      }
      return
    }
    this._queue = playable
    void this.emit(PlaybackScheduleMaterializationEvent.QUEUE_UPDATE, this._queue)
    this._observeQueueDurationInvariants()
  }

  private _cachedAssetUrns(): Set<string> {
    const urns = new Set<string>()
    for (const asset of this._assets) {
      if (typeof asset.urn === 'string' && asset.urn !== '') {
        urns.add(asset.urn)
      }
    }
    return urns
  }

  private _playableQueue(scheduled: readonly IQueueFrame[]) {
    const root = this.playback.getPlaybackScheduleRootEffective()
    const schedule = isScheduleRootAssignment(root) ? root : null
    return filterQueueToCachedMedia(
      scheduled,
      this._cachedAssetUrns(),
      collectReadyWithoutBinaryUrns(schedule?.widgetAssetMetadata, schedule?.hlsVideoAssetMetadata)
    )
  }

  private _observeQueueDurationInvariants(): void {
    runPlayerDiagnosticsSafely(() => {
      const scheduleRoot = this.playback.scheduleRoot
      observeQueueDurationSpineInvariant({
        audit: this.playback.getLastQueueDurationSpineAudit(),
        materializedQueue: this._scheduledQueue,
        scheduleRootUrn: scheduleRoot?.scheduleRootUrn ?? null,
        operatorMasterPlaylistUrn: this.playback.getOperatorMasterPlaylistUrn()
      })
    })
  }

  get assets() {
    return this._assets
  }

  set assets(assets: TCampaignAsset[]) {
    this._assets = assets
    if (
      this._scheduledQueue.length > 0
      && (this._state === PlaybackScheduleMaterializationState.READY
        || this._state === PlaybackScheduleMaterializationState.ERROR)
    ) {
      this.queue = this._scheduledQueue
    }
    void this.emit(PlaybackScheduleMaterializationEvent.ASSETS_UPDATE, this._assets)
  }

  private stopQueueRebuildTick(): void {
    if (this._queueRebuildIntervalId !== null) {
      clearInterval(this._queueRebuildIntervalId)
      this._queueRebuildIntervalId = null
    }
  }

  private syncQueueRebuildScheduleFingerprint(): void {
    const root = this.playback.getPlaybackScheduleRootEffective()
    this._lastQueueRebuildScheduleFp = root != null
      ? this.playback.scheduleSpineFingerprintForRoot(root)
      : null
  }

  private startQueueRebuildTick(): void {
    this.stopQueueRebuildTick()
    this.syncQueueRebuildScheduleFingerprint()
    this._queueRebuildIntervalId = setInterval(() => {
      const root = this.playback.getPlaybackScheduleRootEffective()
      if (root === null) {
        return
      }
      const previousSeq = getQueueSequenceHash(this._scheduledQueue)
      const peekFrames = this.playback.peekQueueFramesNow()
      if (isSameQueueSequence(this._scheduledQueue, peekFrames)) {
        this.syncQueueRebuildScheduleFingerprint()
        return
      }
      if (this.playback.peekPlaybackSequenceHashNow() === previousSeq) {
        this.syncQueueRebuildScheduleFingerprint()
        return
      }
      const rebuilt = this.playback.rebuildQueue()
      this.syncQueueRebuildScheduleFingerprint()
      if (isSameQueueSequence(this._scheduledQueue, rebuilt)) {
        return
      }
      /**
       * Single-frame house rotating embed: picks advance only on full loop wrap
       * ({@link refreshEmbeddedPicksAfterLoopWrap}), not on interval rebuilds — emitting here caused mid-dwell
       * QUEUE_UPDATE with the next editorial frame and wrong worker durations.
       */
      if (this._scheduledQueue.length === 1 && rebuilt.length === 1) {
        return
      }
      this.queue = rebuilt
    }, QUEUE_REBUILD_INTERVAL_MS)
  }

  async setPlaybackSchedule(
    root: IPlaybackScheduleRoot | null,
    opts?: TSetPlaybackScheduleOptions
  ) {
    this._coalescePending = root
    if (opts?.forceAssetReload === true) {
      this._coalesceForceAssetReload = true
    }
    // Always replace (or clear) — do not leave a prior soft-defer head gate on a later root.
    this._coalesceAwaitAssetUrns
      = opts?.awaitAssetUrns != null && opts.awaitAssetUrns.length > 0
        ? opts.awaitAssetUrns
        : undefined
    this._setPlaybackScheduleChain = this._setPlaybackScheduleChain
      .then(() => this._drainCoalescedSchedule())
      .catch((err: unknown) => {
        this.logger.error(err)
      })
    await this._setPlaybackScheduleChain
  }

  /**
   * Runs {@link _setPlaybackScheduleImpl} for the pending root, then repeats if another call set
   * {@link _coalescePending} while the previous impl was in flight — avoids N full loads for N rapid registration
   * updates where only the final schedule matters.
   */
  private async _drainCoalescedSchedule(): Promise<void> {
    for (;;) {
      if (this._coalescePending === undefined) {
        return
      }
      const root = this._coalescePending
      const forceAssetReload = this._coalesceForceAssetReload
      const awaitAssetUrns = this._coalesceAwaitAssetUrns
      this._coalescePending = undefined
      this._coalesceForceAssetReload = false
      this._coalesceAwaitAssetUrns = undefined
      await this._setPlaybackScheduleImpl(root, {
        forceAssetReload,
        ...(awaitAssetUrns != null && !forceAssetReload ? { awaitAssetUrns } : {})
      })
    }
  }

  /** True when every schedule-referenced binary URN is already present in {@link _assets}. */
  private _materializedAssetsCoverSchedule(root: IPlaybackScheduleRoot | null): boolean {
    if (root == null) {
      return true
    }
    const needed = collectScheduleRootAssetUrns(root)
    if (needed.size === 0) {
      return true
    }
    const have = new Set(this._assets.map(a => a.urn))
    for (const urn of needed) {
      if (!have.has(urn)) {
        return false
      }
    }
    return true
  }

  private async _setPlaybackScheduleImpl(
    root: IPlaybackScheduleRoot | null,
    opts?: TSetPlaybackScheduleOptions
  ): Promise<void> {
    const forceAssetReload = opts?.forceAssetReload === true
    const prevRoot = this.playback.getLastSetPlaybackRoot()
    const prevId = getPlaybackScheduleMaterializationIdentity(prevRoot)
    const nextId = getPlaybackScheduleMaterializationIdentity(root)
    /**
     * Same {@link getPlaybackScheduleMaterializationIdentity} (e.g. one `player-master-*` playlist row) can still
     * ship new `embeddedPlaylistFrames` / facets when an ad embedding is removed. Without resetting, module-level
     * random/rotating embedded picks keep stale frame ids → wrong or “unknown” creatives after order stop.
     */
    const scheduleJsonUnchanged = prevRoot != null && root != null && canonicalStringify(prevRoot) === canonicalStringify(root)
    /**
     * Registration-status often re-sends the same schedule with different top-level metadata (`widgetAssetMetadata`,
     * proof maps, etc.). Full-JSON compare used to clear embed picks every poll → flicker.
     * Reset embed state only when the schedule spine ({@link scheduleFingerprint}) or `embeddedPlaylistFrames` body
     * actually changes; {@link getPlaybackScheduleMaterializationIdentity} already gates identity.
     */
    const spineAt = getLocalAsUtcInstant()
    const spineUnchanged = prevRoot != null && root != null
      && this.playback.scheduleSpineFingerprintForRoot(prevRoot, spineAt)
      === this.playback.scheduleSpineFingerprintForRoot(root, spineAt)
    const embeddedSelectionUnchanged = registrationScheduleFrameOverlaysFingerprint(prevRoot)
      === registrationScheduleFrameOverlaysFingerprint(root)
    const materializationIdentityUnchanged = prevId != null && nextId != null && prevId === nextId
    const embedSelectionMustReset = prevRoot == null
      ? root != null
      : !(materializationIdentityUnchanged && embeddedSelectionUnchanged && spineUnchanged)
    if (embedSelectionMustReset) {
      resetEmbeddedFrameSelectionState()
    }
    /**
     * Same schedule spine + same `embeddedPlaylistFrames` map but full JSON differs (registration-status metadata
     * churn). {@link peekPlaybackSequenceHashNow} can still differ after {@link PlaybackScheduleLoader.set} because
     * random/rotating embed resolution is not part of {@link scheduleFingerprint} — that used to force LOADING and
     * visible flicker on every poll.
     */
    const previousQueueSeqHash = getQueueSequenceHash(this._scheduledQueue)
    const canDeferLoadingChrome
      = root !== null
        && prevId !== null
        && prevId === nextId
        && this._queue.length > 0
        && this._state === PlaybackScheduleMaterializationState.READY

    this.playback.set(root)
    this.stopQueueRebuildTick()
    try {
      const peekSeq = this.playback.peekPlaybackSequenceHashNow()
      /**
       * Soft-skip only when the on-air queue already matches the peeked sequence.
       * Metadata-only polls must still re-load when JSON changed so widget/HLS metadata
       * updates reach fetchAssets (URN presence alone is not enough).
       */
      const softSamePlaybackSequence
        = canDeferLoadingChrome
          && previousQueueSeqHash === peekSeq
          && scheduleJsonUnchanged

      /**
       * WS reconnect / registration poll re-applies an identical schedule root. Skipping `load()` avoids
       * redundant `fetchAssets` (Baixando mídias) when we already hold every URN in memory (#1356).
       * Wake / cache-repair pass {@link TSetPlaybackScheduleOptions.forceAssetReload}.
       */
      if (
        !forceAssetReload
        && softSamePlaybackSequence
        && this._materializedAssetsCoverSchedule(root)
      ) {
        if (root !== null) {
          this.startQueueRebuildTick()
        }
        return
      }

      /**
       * When we are already READY with frames on screen, emitting LOADING only flips {@link usePlayer}’s
       * `loadingCampaign` and related UI without changing the root layer (idle vs VBuffer is driven by queue length).
       * Skipping LOADING keeps the prior schedule visible until this load finishes — removes visible in-playback flicker
       * on registration refresh / operator-default churn while `await this.playback.load()` runs.
       */
      const suppressLoadingChromeWhilePlaying
        = root !== null
          && this._queue.length > 0
          && this._state === PlaybackScheduleMaterializationState.READY

      const willEmitLoadingState
        = !softSamePlaybackSequence && !suppressLoadingChromeWhilePlaying

      if (willEmitLoadingState) {
        this.state = PlaybackScheduleMaterializationState.LOADING
      }
      // Normal rematch/reload trusts healthy IDB; wake/cache-repair still revalidates etags.
      // Soft-defer passes awaitAssetUrns so rematch is not blocked on campaign-tail download.
      await this.playback.load({
        revalidateCachedEtags: forceAssetReload,
        ...(opts?.awaitAssetUrns != null && opts.awaitAssetUrns.length > 0 && !forceAssetReload
          ? { awaitAssetUrns: opts.awaitAssetUrns }
          : {})
      })
      const { queue, assets } = this.playback
      const assetsChanged = !areCampaignAssetListsEquivalent(this._assets, assets)
      // Assets first so the playable filter sees binaries that just finished downloading.
      this._assets = assets
      this.queue = queue
      if (assetsChanged) {
        void this.emit(PlaybackScheduleMaterializationEvent.ASSETS_UPDATE, this._assets)
      }
      this.state = PlaybackScheduleMaterializationState.READY
      if (root !== null) {
        this.startQueueRebuildTick()
      }
    }
    catch (err) {
      this.logger.error(err)
      const message = err instanceof Error ? err.message : String(err)
      void this.emit(PlaybackScheduleMaterializationEvent.LOAD_ERROR, { message })
      this.state = PlaybackScheduleMaterializationState.ERROR
      if (root !== null) {
        const rebuilt = this.playback.rebuildQueue()
        const a = this.playback.assets
        const assetsChanged = !areCampaignAssetListsEquivalent(this._assets, a)
        this._assets = a
        this.queue = rebuilt
        if (assetsChanged) {
          void this.emit(PlaybackScheduleMaterializationEvent.ASSETS_UPDATE, this._assets)
        }
        this.startQueueRebuildTick()
      }
    }
  }

  getPlaybackScheduleRootEffective() {
    return this.playback.getPlaybackScheduleRootEffective()
  }

  /** Last root passed to {@link setPlaybackSchedule}; used to compare registration updates without re-loading. */
  getLastSetPlaybackRoot(): IPlaybackScheduleRoot | null {
    return this.playback.getLastSetPlaybackRoot()
  }

  getWorkerPlaybackPlanNow(): PlaybackSegment[] {
    return this.playback.getWorkerPlaybackPlanNow()
  }

  /**
   * Sequence hash for the configured root at “now” (same spine used by soft-skip / rebuild tick).
   * Prefer this over {@link peekQueueFramesNow} for desync checks — rotating embeds can differ frame ids
   * while the sequence hash still matches the on-air queue.
   */
  peekPlaybackSequenceHashNow(): string {
    return this.playback.peekPlaybackSequenceHashNow()
  }

  /** Peek materialization queue for the configured root without committing embed decks. */
  peekQueueFramesNow(): IQueueFrame[] {
    return this.playback.peekQueueFramesNow()
  }

  /** @returns true when the on-air frame id changed (rotating/random embed advanced). */
  refreshEmbeddedPicksAfterLoopWrap(): boolean {
    const rebuilt = this.playback.rebuildQueueClearingEmbeddedRandomCache()
    if (isSameQueueSequence(this._scheduledQueue, rebuilt)) {
      if (getQueueHash(this._scheduledQueue) !== getQueueHash(rebuilt)) {
        this.queue = rebuilt
      }
      else if (this._scheduledQueue !== rebuilt) {
        this._scheduledQueue = rebuilt
      }
      return false
    }
    this.queue = rebuilt
    return true
  }

  /**
   * When the tenant enables viewport-matched frames only, pass a singleton set of the device {@link AspectRatio}.
   * Pass `null` to disable filtering. Rebuilds the queue when a schedule is already loaded.
   */
  setAllowedAspectRatiosForPlayback(allowed: ReadonlySet<AspectRatio> | null): void {
    this.playback.setAllowedAspectRatiosForPlayback(allowed)
    if (this.playback.getPlaybackScheduleRootEffective() === null) {
      return
    }
    this.queue = this.playback.rebuildQueue()
  }

  /** Low-capacity video skip: omit frames with any video facet from the queue. */
  setExcludeFramesWithVideoForPlayback(exclude: boolean): void {
    this.playback.setExcludeFramesWithVideoForPlayback(exclude)
    if (this.playback.getPlaybackScheduleRootEffective() === null) {
      return
    }
    this.queue = this.playback.rebuildQueue()
  }

  /** Lucimark Book paid orders always match device viewport when registered. */
  setPortalBookViewportAspectForPlayback(aspect: AspectRatio | null): void {
    this.playback.setPortalBookViewportAspectForPlayback(aspect)
    if (this.playback.getPlaybackScheduleRootEffective() === null) {
      return
    }
    this.queue = this.playback.rebuildQueue()
  }

  setOperatorMasterPlaylistUrn(urn: string | null): void {
    this.playback.setOperatorMasterPlaylistUrn(urn)
    if (this.playback.getPlaybackScheduleRootEffective() === null) {
      return
    }
    this.queue = this.playback.rebuildQueue()
  }

  /**
   * Update geo point used for assignment geofilters and rebuild when a schedule is loaded.
   * Debounced by callers when GPS updates frequently.
   */
  setGeoPointForPlayback(
    point: import('@lucimark/shared/models/playlist-assignment-geo-filter').TGeoLatLng | null
  ): void {
    const prev = this.playback.getGeoPointForPlayback()
    const unchanged = prev?.latitude === point?.latitude && prev?.longitude === point?.longitude
    this.playback.setGeoPointForPlayback(point)
    if (unchanged || this.playback.getPlaybackScheduleRootEffective() === null) {
      return
    }
    this.queue = this.playback.rebuildQueue()
  }

  /** Live resolver options for soft-defer head prefetch (must match cutover). */
  getResolveSegmentOptions() {
    return this.playback.getResolveSegmentOptions()
  }
}
